import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { customerBrandStyles } from '../../styles/customer-brand-styles';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
import { setUserContextMode } from '../../state/user-context-mode';
import { resolveRouterPath, router } from '../../router';
import { ApiFactory } from '../../api/easypos-api';
import { UserProfile } from '../../datatypes/user-profile';
import { money } from '../../datatypes/currency';
import type {
  IShopLoyaltyMembership,
  ICustomerPurchase,
} from '../../api/model/requests';
import { themeStore, type ThemeChoice } from '../../theme/theme-store';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/menu-label/menu-label.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/qr-code/qr-code.js';
import { encodeMemberQr } from '../../datatypes/member-code';

// Loyalty card gradients — rotated so each shop's card feels distinct,
// like differently-coloured cards in a physical wallet. Gold-dominant
// (matches Plan B: gold primary on customer side) with one each of:
// navy/gold executive, festive banderita sunset, tropical leaf, heart
// of gold (the rose accent for the "love" card), and earth/mocha for
// the rooted Filipino warmth.
const CARD_GRADIENTS = [
  // 1. Gold sunrise — the default loyalty look
  {
    from: '#ffc83d',
    to: '#d99820',
    text: '#0c1322',
    glow: 'rgba(217, 152, 32, 0.32)',
  },
  // 2. Navy & gold — premium executive
  {
    from: '#0f2a44',
    to: '#1a3e5e',
    text: '#ffc83d',
    glow: 'rgba(15, 42, 68, 0.32)',
  },
  // 3. Banderita sunset — gold to festive coral
  {
    from: '#ffd23f',
    to: '#ff7a4b',
    text: '#0c1322',
    glow: 'rgba(255, 122, 75, 0.32)',
  },
  // 4. Tropical leaf — malong abundance
  {
    from: '#4a8c5a',
    to: '#2f6b3f',
    text: '#ffc83d',
    glow: 'rgba(74, 140, 90, 0.32)',
  },
  // 5. Heart of gold — the rose accent card (love)
  {
    from: '#ff4f8b',
    to: '#d99820',
    text: '#ffffff',
    glow: 'rgba(255, 79, 139, 0.32)',
  },
  // 6. Mocha & gold — earthy moreno warmth
  {
    from: '#6b4423',
    to: '#a47554',
    text: '#ffc83d',
    glow: 'rgba(107, 68, 35, 0.32)',
  },
];

@customElement('customer-home')
export class CustomerHome extends WithUserProfileConsumer(LitElement) {
  @state() private _enrolling = false;
  @state() private _enrollError: string | null = null;
  @state() private _memberships: IShopLoyaltyMembership[] | null = null;
  @state() private _membershipsLoading = false;
  // Filter for the memberships list — surfaces once a customer has joined many
  // shops (a wallet can grow long over time).
  @state() private _memberFilter = '';
  // Which membership card currently has its wallet QR expanded (one at a
  // time). Keyed by shopName; null = all collapsed.
  @state() private _qrShop: string | null = null;
  // Which card has its purchase history expanded + the fetched purchases.
  @state() private _purchasesShop: string | null = null;
  @state() private _purchases: ICustomerPurchase[] | null = null;
  @state() private _purchasesLoading = false;
  // The purchase opened as a full receipt overlay (tap a purchase row), plus
  // the membership it belongs to (for the shop + member header). _receipt !=
  // null = overlay open.
  @state() private _receipt: ICustomerPurchase | null = null;
  private _receiptMembership: IShopLoyaltyMembership | null = null;
  @state() private _joinCode = '';
  @state() private _joinLookup: {
    shopName: string;
    shopDisplayName: string;
  } | null = null;
  @state() private _joinLooking = false;
  @state() private _joinLookError: string | null = null;
  @state() private _joining = false;
  @state() private _joinError: string | null = null;
  @state() private _themeChoice: ThemeChoice = themeStore.choice;
  private _unsubTheme: (() => void) | null = null;
  private _hasInitialized = false;

  static styles = [
    customerBrandStyles,
    css`
      :host {
        display: block;
      }

      .page {
        padding-bottom: 3rem;
      }
      /* One subtle atmospheric blob — used strategically, not as wallpaper. */
      .blob.a {
        top: -220px;
        right: -180px;
        width: 380px;
        height: 380px;
        background: var(--coral);
        opacity: 0.16;
      }

      .page-brand {
        z-index: 3;
      }
      .menu-trigger {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: var(--canvas);
        border: 1px solid var(--line);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: var(--ink);
        font-size: 18px;
        box-shadow: var(--shadow-soft);
        transition:
          transform 140ms ease,
          box-shadow 140ms ease;
      }
      .menu-trigger:hover {
        transform: translateY(-1px);
        box-shadow: var(--shadow-lift);
      }

      .content {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: 600px;
        margin: 0 auto;
        padding: 8px clamp(1.25rem, 5vw, 2rem) 1rem;
        box-sizing: border-box;
      }

      /* === Hero ============================================ */
      .hero {
        margin-top: 1rem;
        margin-bottom: 2rem;
      }
      .hero h1 {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: clamp(1.85rem, 6.5vw, 2.6rem);
        line-height: 1.05;
        letter-spacing: -0.03em;
        margin: 0 0 0.35rem;
        color: var(--ink);
      }
      .hero h1 .wave {
        display: inline-block;
        font-weight: 400;
        margin-left: 0.15em;
        transform-origin: 70% 70%;
        animation: wave 2.6s ease-in-out infinite;
      }
      .hero p {
        font-size: 1rem;
        font-weight: 500;
        color: var(--ink-soft);
        margin: 0;
      }

      /* === Section ========================================= */
      .section {
        margin-bottom: 2.25rem;
      }
      .section-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        margin-bottom: 1rem;
      }
      .section-count {
        font-size: 13px;
        font-weight: 700;
        color: var(--ink-faint);
        font-feature-settings: 'tnum';
      }

      /* === Loyalty cards (actual gradient cards) =========== */
      .members-stack {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
      .member-search {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.9rem;
        padding: 0.55rem 0.85rem;
        border: 1px solid var(--sl-color-neutral-300, rgba(15, 30, 55, 0.12));
        border-radius: 999px;
        background: var(--sl-color-neutral-0, #fff);
        color: var(--sl-color-neutral-500, #777);
      }
      .member-search input {
        flex: 1 1 auto;
        border: none;
        outline: none;
        background: transparent;
        font: inherit;
        color: var(--sl-color-neutral-900, #111);
      }
      .member-search-empty {
        padding: 1.2rem;
        text-align: center;
        color: var(--sl-color-neutral-500, #777);
        font-size: 0.9rem;
      }
      .member-card {
        position: relative;
        padding: 1.5rem 1.4rem 1.4rem;
        border-radius: 20px;
        background: linear-gradient(135deg, var(--g-from), var(--g-to));
        color: var(--g-text);
        box-shadow:
          0 12px 28px var(--g-glow),
          0 2px 6px rgba(12, 19, 34, 0.06);
        overflow: hidden;
        min-height: 140px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        transition: transform 240ms cubic-bezier(0.16, 0.84, 0.44, 1);
        cursor: default;
      }
      .member-card:hover {
        transform: translateY(-3px) rotate(-0.4deg);
      }

      /* Decorative ring orb in the corner */
      .member-card::before {
        content: '';
        position: absolute;
        top: -60px;
        right: -60px;
        width: 180px;
        height: 180px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.12);
      }
      .member-card::after {
        content: '';
        position: absolute;
        top: -30px;
        right: -30px;
        width: 120px;
        height: 120px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.12);
      }
      .member-top {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 0.75rem;
      }
      .member-top-info {
        flex: 1;
        min-width: 0;
      }
      .member-eyebrow {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        opacity: 0.75;
        margin-bottom: 4px;
      }
      .member-name {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: clamp(1.15rem, 4.5vw, 1.45rem);
        line-height: 1.1;
        letter-spacing: -0.02em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .member-heart {
        font-size: 22px;
        opacity: 0.9;
      }
      .member-foot {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 1rem;
      }
      .member-since {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.02em;
        opacity: 0.8;
      }
      .member-status {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        background: rgba(255, 255, 255, 0.22);
        border-radius: 999px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }
      .member-status sl-icon {
        font-size: 11px;
      }

      /* Member code + wallet QR (shown at the till) */
      .member-code-row {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-top: 0.9rem;
      }
      .member-code-label {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        opacity: 0.8;
      }
      .member-code-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 5px 12px;
        border: none;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.22);
        color: inherit;
        font: inherit;
        cursor: pointer;
      }
      .member-code {
        font-family: var(
          --font-mono,
          ui-monospace,
          SFMono-Regular,
          Menlo,
          monospace
        );
        font-weight: 700;
        font-size: 14px;
        letter-spacing: 0.14em;
      }
      .member-code-btn sl-icon {
        font-size: 15px;
      }
      .member-qr {
        position: relative;
        z-index: 1;
        margin-top: 0.85rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 14px;
        background: #ffffff;
        border-radius: 16px;
      }
      .member-qr-hint {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5b6781;
      }

      /* Purchase history toggle + panel on a loyalty card */
      .purchases-toggle {
        position: relative;
        z-index: 1;
        margin-top: 0.85rem;
        width: 100%;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 9px 12px;
        border: none;
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.16);
        color: inherit;
        font: inherit;
        font-weight: 700;
        font-size: 13px;
        cursor: pointer;
      }
      .purchases-toggle .chev {
        margin-left: auto;
        font-size: 15px;
        opacity: 0.8;
      }
      .purchases-toggle sl-icon {
        font-size: 16px;
      }
      .purchases-panel {
        position: relative;
        z-index: 1;
        margin-top: 8px;
        background: #ffffff;
        border-radius: 12px;
        padding: 4px 12px;
        color: #1f2a44;
        max-height: 260px;
        overflow-y: auto;
      }
      .purchases-panel.empty {
        padding: 16px 12px;
        text-align: center;
        font-size: 13px;
        color: #5b6781;
      }
      .purchase-row {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 2px;
        border: none;
        border-bottom: 1px solid #eef0f4;
        background: none;
        width: 100%;
        text-align: left;
        font: inherit;
        color: inherit;
        cursor: pointer;
        border-radius: 6px;
      }
      .purchase-row:last-child {
        border-bottom: none;
      }
      .purchase-row:hover {
        background: #f6f7fb;
      }
      .purchase-row:focus-visible {
        outline: 2px solid #c9a227;
        outline-offset: 1px;
      }

      /* --- Receipt overlay (tap a purchase) --- */
      .receipt-overlay {
        position: fixed;
        inset: 0;
        z-index: 50;
        background: rgba(15, 20, 40, 0.55);
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding: 0;
      }
      .receipt-sheet {
        background: #fff;
        color: #1f2a44;
        width: 100%;
        max-width: 460px;
        height: 100vh;
        height: 100dvh;
        display: flex;
        flex-direction: column;
      }
      @media (min-width: 520px) {
        .receipt-overlay {
          align-items: center;
          padding: 24px;
        }
        .receipt-sheet {
          height: auto;
          max-height: 90dvh;
          border-radius: 18px;
          overflow: hidden;
        }
      }
      .receipt-scroll {
        flex: 1 1 auto;
        overflow-y: auto;
        padding: calc(18px + env(safe-area-inset-top)) 18px 18px;
      }
      .receipt {
        max-width: 340px;
        margin: 0 auto;
        font-family: ui-monospace, 'SFMono-Regular', 'Courier New', monospace;
      }
      .r-head {
        text-align: center;
      }
      .r-shop {
        font-size: 19px;
        font-weight: 800;
      }
      .r-sub {
        font-size: 12px;
        color: #5b6781;
        margin-top: 2px;
      }
      .r-meta {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: #5b6781;
        border-bottom: 1px dashed #b9c0d0;
        padding: 8px 0;
        margin: 8px 0;
      }
      .r-line {
        padding: 4px 0;
      }
      .r-line-main {
        display: flex;
        gap: 8px;
        font-size: 13px;
        font-variant-numeric: tabular-nums;
      }
      .r-qty {
        font-weight: 700;
        color: #5b6781;
      }
      .r-name {
        flex: 1 1 auto;
      }
      .r-amt {
        white-space: nowrap;
        font-weight: 600;
      }
      .r-mods {
        font-size: 12px;
        color: #5b6781;
        padding-left: 22px;
      }
      .r-totals {
        border-top: 1px dashed #b9c0d0;
        margin-top: 8px;
        padding-top: 8px;
        font-variant-numeric: tabular-nums;
      }
      .r-totals > div {
        display: flex;
        justify-content: space-between;
        font-size: 13px;
        padding: 2px 0;
      }
      .r-grand {
        font-weight: 800;
        font-size: 15px;
        border-top: 1px solid #1f2a44;
        margin-top: 4px;
        padding-top: 4px;
      }
      .r-pay {
        color: #5b6781;
      }
      .r-foot {
        text-align: center;
        font-size: 12px;
        color: #5b6781;
        margin-top: 12px;
      }
      .receipt-actions {
        flex: none;
        display: flex;
        gap: 10px;
        padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
        border-top: 1px solid #eef0f4;
      }
      .r-btn {
        flex: 1 1 auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 12px;
        border-radius: 12px;
        font: inherit;
        font-weight: 700;
        cursor: pointer;
        border: 1px solid transparent;
      }
      .r-btn.ghost {
        background: #eef0f4;
        color: #1f2a44;
      }
      .r-btn.solid {
        background: #c9a227;
        color: #1b1606;
      }
      .purchase-row__main {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .purchase-when {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.02em;
        color: #5b6781;
      }
      .purchase-items {
        font-size: 13px;
        color: #1f2a44;
        line-height: 1.3;
      }
      .purchase-total {
        flex: none;
        font-weight: 800;
        font-variant-numeric: tabular-nums;
        color: #1f2a44;
      }

      /* "Run a shop?" escape hatch — a customer can also be an owner. */
      .run-a-shop {
        margin-top: 1.5rem;
        text-align: center;
        font-size: 0.9rem;
        color: var(--ink-soft, #5b6781);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
      }
      .run-a-shop__btn {
        appearance: none;
        border: 1.5px solid var(--ink-soft, #5b6781);
        background: transparent;
        color: inherit;
        font: inherit;
        font-weight: 700;
        padding: 8px 18px;
        border-radius: 999px;
        cursor: pointer;
      }
      .run-a-shop__btn:hover {
        border-color: var(--ink, #1f2a44);
      }

      /* === Empty state ===================================== */
      .empty {
        position: relative;
        background: linear-gradient(135deg, var(--tint-sun), var(--tint-coral));
        border-radius: 20px;
        padding: 2.5rem 1.5rem 2.25rem;
        text-align: center;
        overflow: hidden;
      }
      .empty .heart-orb {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: var(--coral);
        color: white;
        font-size: 26px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1rem;
        box-shadow: 0 8px 20px var(--coral-glow);
      }
      .empty h3 {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: 1.35rem;
        margin: 0 0 0.4rem;
        color: var(--ink);
        letter-spacing: -0.02em;
      }
      .empty p {
        margin: 0 auto;
        max-width: 280px;
        font-size: 0.92rem;
        font-weight: 500;
        color: var(--ink-soft);
        line-height: 1.5;
      }

      /* === Join card ======================================= */
      .join-card {
        background: var(--canvas);
        border: 1px solid var(--line);
        border-radius: 22px;
        padding: 1.5rem 1.5rem 1.4rem;
        box-shadow: var(--shadow-soft);
      }
      .join-title {
        font-family: var(--font-display);
        font-weight: 800;
        font-size: clamp(1.25rem, 5vw, 1.5rem);
        line-height: 1.1;
        letter-spacing: -0.025em;
        margin: 0 0 0.3rem;
      }
      .join-title .accent {
        color: var(--sun-deep);
      }
      .join-hint {
        font-size: 0.92rem;
        color: var(--ink-soft);
        font-weight: 500;
        margin: 0 0 1.1rem;
      }

      .code-input-row {
        display: flex;
        gap: 0.6rem;
        align-items: stretch;
      }
      .code-input-row .pa-btn {
        flex: none;
      }
      @media (max-width: 420px) {
        .code-input-row {
          flex-direction: column;
        }
        .code-input-row .pa-btn {
          width: 100%;
        }
      }
      .code-input {
        flex: 1;
        min-width: 0;
        font-family: var(--font-numeric);
        font-feature-settings: 'tnum';
        font-size: 1.4rem;
        font-weight: 800;
        letter-spacing: 0.12em;
        text-align: center;
        padding: 0.85rem 0.8rem;
        border: 2px solid var(--line);
        border-radius: 14px;
        background: var(--canvas-soft);
        color: var(--ink);
        outline: none;
        transition:
          border-color 140ms ease,
          background 140ms ease;
      }
      .code-input:focus {
        border-color: var(--sun-deep);
        background: var(--canvas);
        box-shadow: 0 0 0 4px var(--sun-glow);
      }
      .code-input::placeholder {
        color: var(--ink-faint);
        font-weight: 500;
        letter-spacing: 0.08em;
      }

      .join-preview {
        margin-top: 0.85rem;
        padding: 0.85rem 1rem;
        background: var(--tint-sun);
        border-radius: 14px;
        display: flex;
        align-items: center;
        gap: 0.8rem;
      }
      .join-preview-mark {
        width: 38px;
        height: 38px;
        border-radius: 12px;
        background: var(--sun-deep);
        color: white;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: none;
        font-size: 17px;
      }
      .join-preview-name {
        flex: 1;
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 1rem;
        color: var(--ink);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .alert-warm {
        margin-top: 0.85rem;
        padding: 0.75rem 0.95rem;
        background: var(--tint-coral);
        color: var(--coral-deep);
        border-radius: 12px;
        font-size: 0.88rem;
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 0.55rem;
      }

      .loading-row {
        display: inline-flex;
        align-items: center;
        gap: 0.6rem;
        color: var(--ink-soft);
        font-size: 0.95rem;
        font-weight: 500;
        padding: 1rem 0;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    this._unsubTheme = themeStore.subscribe((_eff, choice) => {
      this._themeChoice = choice;
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubTheme?.();
    this._unsubTheme = null;
  }
  protected updated(_changed: Map<string | symbol, unknown>) {
    if (!this._hasInitialized && this.userProfile) {
      this._hasInitialized = true;
      void this._initialize();
    }
  }
  private async _initialize() {
    if (!this.userProfile?.isCustomer) {
      await this._enroll();
    } else {
      await this._loadMemberships();
    }
  }
  private async _enroll() {
    this._enrolling = true;
    this._enrollError = null;
    const r = await ApiFactory.authenticated().enrollAsCustomer();
    this._enrolling = false;
    if (!r.success || !r.data) {
      this._enrollError = r.error ?? 'Could not enroll as a customer';
      return;
    }
    const currentProfile = this.userProfile;
    if (!currentProfile) return;
    const newProfile = new UserProfile({
      ...currentProfile.profile,
      accessToken: r.data,
    });
    window.dispatchEvent(
      new CustomEvent('user-profile-set', { detail: newProfile })
    );
    await this._loadMemberships();
  }
  private async _loadMemberships() {
    this._membershipsLoading = true;
    const r = await ApiFactory.authenticated().getCustomerMemberships();
    this._membershipsLoading = false;
    if (r.success && r.data) this._memberships = r.data.filter((m) => m.active);
  }
  private async _lookupShop() {
    const code = this._joinCode.trim();
    if (!code) return;
    this._joinLooking = true;
    this._joinLookup = null;
    this._joinLookError = null;
    const r = await ApiFactory.anonymous().lookupShopByCode(code);
    this._joinLooking = false;
    if (!r.success || !r.data) {
      this._joinLookError = `No shop found for code "${code}".`;
      return;
    }
    this._joinLookup = r.data;
  }
  private async _joinShop() {
    if (!this._joinLookup) return;
    this._joining = true;
    this._joinError = null;
    const r = await ApiFactory.authenticated().joinShopByCode(
      this._joinCode.trim()
    );
    this._joining = false;
    if (!r.success || !r.data) {
      this._joinError = r.error ?? 'Could not join shop';
      return;
    }
    await this._loadMemberships();
    this._joinCode = '';
    this._joinLookup = null;
  }
  private _switchToShop() {
    setUserContextMode('shop');
    router.navigate(resolveRouterPath('dashboard'));
  }
  private _logout() {
    window.dispatchEvent(
      new CustomEvent('logout-requested', { bubbles: true, composed: true })
    );
  }
  private _themeIcon(): string {
    if (this._themeChoice === 'light') return 'sun';
    if (this._themeChoice === 'dark') return 'moon';
    return 'circle-half';
  }
  private _themeLabel(): string {
    if (this._themeChoice === 'light') return 'Theme · light';
    if (this._themeChoice === 'dark') return 'Theme · dark';
    return 'Theme · auto';
  }

  private _toggleQr(shopName: string) {
    this._qrShop = this._qrShop === shopName ? null : shopName;
  }

  private async _togglePurchases(shopName: string) {
    if (this._purchasesShop === shopName) {
      this._purchasesShop = null;
      return;
    }
    this._purchasesShop = shopName;
    this._purchases = null;
    this._purchasesLoading = true;
    const r = await ApiFactory.authenticated().getCustomerPurchases(shopName);
    // Guard against a fast re-toggle to a different card while loading.
    if (this._purchasesShop !== shopName) return;
    this._purchases = r.success && r.data ? r.data : [];
    this._purchasesLoading = false;
  }

  private _renderPurchases(m: IShopLoyaltyMembership) {
    if (this._purchasesLoading) {
      return html`<div class="purchases-panel">
        <sl-spinner></sl-spinner> Loading your purchases…
      </div>`;
    }
    const list = this._purchases ?? [];
    if (list.length === 0) {
      return html`<div class="purchases-panel empty">
        No purchases here yet.
      </div>`;
    }
    return html`
      <div class="purchases-panel">
        ${list.map((p) => {
          const when = new Date(p.createdUtc).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          });
          const items = p.items
            .map(
              (it) =>
                `${it.quantity}× ${it.name}${it.variantLabel ? ` (${it.variantLabel})` : ''}`
            )
            .join(', ');
          return html`
            <button
              class="purchase-row"
              @click=${() => this._openReceipt(p, m)}
              aria-label="View receipt"
            >
              <div class="purchase-row__main">
                <span class="purchase-when">${when}</span>
                <span class="purchase-items">${items}</span>
              </div>
              <span class="purchase-total">${money(p.total)}</span>
            </button>
          `;
        })}
      </div>
    `;
  }

  private _openReceipt(p: ICustomerPurchase, m: IShopLoyaltyMembership) {
    this._receipt = p;
    this._receiptMembership = m;
  }

  private _closeReceipt() {
    this._receipt = null;
    this._receiptMembership = null;
  }

  private _renderMemberships() {
    if (this._membershipsLoading) {
      return html`<div class="loading-row">
        <sl-spinner></sl-spinner> Loading your memberships…
      </div>`;
    }
    if (!this._memberships || this._memberships.length === 0) {
      return html`
        <div class="empty">
          <div class="heart-orb"><sl-icon name="heart-fill"></sl-icon></div>
          <h3>No memberships yet</h3>
          <p>
            Scan a shop's QR or enter their code below to join your first
            loyalty programme.
          </p>
        </div>
      `;
    }
    const all = this._memberships;
    const SHOW_SEARCH_AT = 8;
    const q = this._memberFilter.trim().toLowerCase();
    const shown = q
      ? all.filter(
          (m) =>
            (m.shopDisplayName || m.shopName).toLowerCase().includes(q) ||
            m.shopName.toLowerCase().includes(q) ||
            (m.memberCode ?? '').toLowerCase().includes(q)
        )
      : all;
    return html`
      ${
        all.length >= SHOW_SEARCH_AT
          ? html`
              <label class="member-search">
                <sl-icon name="search"></sl-icon>
                <input
                  type="search"
                  placeholder="Search your shops…"
                  .value=${this._memberFilter}
                  @input=${(e: Event) => (this._memberFilter = (e.target as HTMLInputElement).value)}
                />
              </label>
            `
          : nothing
      }
      <div class="members-stack">
        ${
          shown.length === 0
            ? html`<div class="member-search-empty">
                No shops match “${this._memberFilter}”.
              </div>`
            : shown.map((m, i) => {
                const g = CARD_GRADIENTS[i % CARD_GRADIENTS.length];
                const since = new Date(m.joinedUtc).toLocaleDateString(
                  undefined,
                  { month: 'short', year: 'numeric' }
                );
                return html`
                  <div
                    class="member-card pa-rise"
                    style="--g-from:${g.from}; --g-to:${g.to}; --g-text:${g.text}; --g-glow:${g.glow}; animation-delay:${i * 60}ms"
                  >
                    <div class="member-top">
                      <div class="member-top-info">
                        <div class="member-eyebrow">Loyalty member</div>
                        <div class="member-name">
                          ${m.shopDisplayName || m.shopName}
                        </div>
                      </div>
                      <span class="member-heart"
                        ><sl-icon name="heart-fill"></sl-icon
                      ></span>
                    </div>
                    <div class="member-foot">
                      <span class="member-since">Joined ${since}</span>
                      <span class="member-status"
                        ><sl-icon name="check-circle-fill"></sl-icon>
                        Active</span
                      >
                    </div>
                    ${
                      m.memberCode
                        ? html`
                            <div class="member-code-row">
                              <span class="member-code-label">Member code</span>
                              <button
                                class="member-code-btn"
                                @click=${() => this._toggleQr(m.shopName)}
                                aria-expanded=${this._qrShop === m.shopName}
                              >
                                <span class="member-code">${m.memberCode}</span>
                                <sl-icon
                                  name=${this._qrShop === m.shopName ? 'chevron-up' : 'qr-code'}
                                ></sl-icon>
                              </button>
                            </div>
                            ${
                              this._qrShop === m.shopName
                                ? html`
                                    <div class="member-qr">
                                      <sl-qr-code
                                        value=${encodeMemberQr(m.memberCode)}
                                        size="170"
                                      ></sl-qr-code>
                                      <span class="member-qr-hint"
                                        >Show this at the till</span
                                      >
                                    </div>
                                  `
                                : nothing
                            }
                          `
                        : nothing
                    }
                    <button
                      class="purchases-toggle"
                      @click=${() => this._togglePurchases(m.shopName)}
                      aria-expanded=${this._purchasesShop === m.shopName}
                    >
                      <sl-icon name="receipt"></sl-icon>
                      <span>Your purchases</span>
                      <sl-icon
                        class="chev"
                        name=${this._purchasesShop === m.shopName ? 'chevron-up' : 'chevron-down'}
                      ></sl-icon>
                    </button>
                    ${this._purchasesShop === m.shopName ? this._renderPurchases(m) : nothing}
                  </div>
                `;
              })
        }
      </div>
    `;
  }

  private _renderJoinSection() {
    const joinLookup = this._joinLookup;
    const alreadyMember =
      joinLookup &&
      (this._memberships ?? []).some((m) => m.shopName === joinLookup.shopName);
    return html`
      <div class="section pa-rise pa-rise-4">
        <div class="join-card">
          <div class="join-title">Join a <span class="accent">shop</span></div>
          <p class="join-hint">
            Got a code from the counter? Type it below or scan the QR.
          </p>
          <div class="code-input-row">
            <input
              class="code-input"
              type="text"
              inputmode="numeric"
              autocomplete="off"
              placeholder="000000"
              .value=${this._joinCode}
              @input=${(e: Event) => {
                this._joinCode = (e.target as HTMLInputElement).value;
                this._joinLookup = null;
                this._joinLookError = null;
              }}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') void this._lookupShop();
              }}
              ?disabled=${this._joinLooking || this._joining}
            />
            <button
              class="pa-btn"
              @click=${this._lookupShop}
              ?disabled=${!this._joinCode.trim() || this._joinLooking || this._joining}
            >
              ${this._joinLooking ? html`<sl-spinner style="font-size:16px;"></sl-spinner>` : html`<sl-icon name="search"></sl-icon>`}
              Find
            </button>
          </div>

          ${
            this._joinLookError
              ? html`
                  <div class="alert-warm">
                    <sl-icon name="exclamation-circle-fill"></sl-icon>
                    ${this._joinLookError}
                  </div>
                `
              : nothing
          }
          ${
            this._joinLookup
              ? html`
                  <div class="join-preview pa-rise">
                    <div class="join-preview-mark">
                      <sl-icon name="shop"></sl-icon>
                    </div>
                    <span class="join-preview-name"
                      >${this._joinLookup.shopDisplayName || this._joinLookup.shopName}</span
                    >
                    ${
                      alreadyMember
                        ? html`<span class="pill mint"
                            ><sl-icon name="check-lg"></sl-icon> Joined</span
                          >`
                        : html`
                            <button
                              class="pa-btn coral"
                              @click=${this._joinShop}
                              ?disabled=${this._joining}
                            >
                              ${this._joining ? html`<sl-spinner style="font-size:16px;"></sl-spinner>` : html`<sl-icon name="heart-fill"></sl-icon>`}
                              Join
                            </button>
                          `
                    }
                  </div>
                `
              : nothing
          }
          ${
            this._joinError
              ? html`
                  <div class="alert-warm">
                    <sl-icon name="exclamation-triangle-fill"></sl-icon>
                    ${this._joinError}
                  </div>
                `
              : nothing
          }
        </div>
      </div>
    `;
  }

  render() {
    const name = this.userProfile?.displayName?.split(' ')[0] ?? '';
    const hasShops = this.userProfile?.hasActiveShops ?? false;
    return html`
      <div class="page">
        <div class="blob a"></div>
        <div class="blob b"></div>
        <div class="page-brand pa-rise">
          <a class="page-brand-mark" href="/customer">
            <img src="/assets/icons/pag-asenso-icon.svg" alt="" />
            <span class="page-brand-wordmark">Pag<span>-asenso</span></span>
          </a>
          <sl-dropdown placement="bottom-end">
            <button slot="trigger" class="menu-trigger" aria-label="Menu">
              <sl-icon name="three-dots"></sl-icon>
            </button>
            <sl-menu>
              ${this.userProfile?.displayName ? html`<sl-menu-label>${this.userProfile.displayName}</sl-menu-label>` : nothing}
              <sl-menu-item @click=${() => themeStore.cycle()}>
                <sl-icon slot="prefix" name=${this._themeIcon()}></sl-icon>
                ${this._themeLabel()}
              </sl-menu-item>
              ${
                hasShops
                  ? html`
                      <sl-divider></sl-divider>
                      <sl-menu-item @click=${this._switchToShop}>
                        <sl-icon slot="prefix" name="shop"></sl-icon>
                        My shop
                      </sl-menu-item>
                    `
                  : nothing
              }
              <sl-divider></sl-divider>
              <sl-menu-item @click=${this._logout}>
                <sl-icon slot="prefix" name="box-arrow-right"></sl-icon>
                Sign out
              </sl-menu-item>
            </sl-menu>
          </sl-dropdown>
        </div>

        <div class="content">
          <div class="hero pa-rise pa-rise-2">
            <h1>${name ? `Hi, ${name}` : 'Hi'}<span class="wave">👋</span></h1>
            <p>Your loyalty rewards and the shops you love.</p>
          </div>

          ${
            this._enrolling
              ? html`
                  <div class="loading-row pa-rise">
                    <sl-spinner></sl-spinner> Setting up your account…
                  </div>
                `
              : this._enrollError
                ? html`
                    <div class="alert-warm pa-rise">
                      <sl-icon name="exclamation-triangle-fill"></sl-icon>
                      ${this._enrollError}
                    </div>
                  `
                : html`
                    <div class="section pa-rise pa-rise-3">
                      <div class="section-head">
                        <span class="eyebrow">Your memberships</span>
                        ${
                          this._memberships && this._memberships.length > 0
                            ? html`<span class="section-count"
                                >${this._memberships.length} active</span
                              >`
                            : nothing
                        }
                      </div>
                      ${this._renderMemberships()}
                    </div>

                    ${this._renderJoinSection()}

                    <div class="run-a-shop">
                      <span>Want to run your own shop?</span>
                      <button
                        class="run-a-shop__btn"
                        @click=${this._goCreateShop}
                      >
                        Set up a shop
                      </button>
                    </div>
                  `
          }
        </div>
      </div>
      ${this._renderReceiptOverlay()}
    `;
  }

  private _renderReceiptOverlay() {
    const p = this._receipt;
    const m = this._receiptMembership;
    if (!p || !m) return nothing;
    const when = new Date(p.createdUtc).toLocaleString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    return html`
      <div class="receipt-overlay" @click=${this._closeReceipt}>
        <div
          class="receipt-sheet"
          role="dialog"
          aria-label="Receipt"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="receipt-scroll">
            <div class="receipt">
              <div class="r-head">
                <div class="r-shop">${m.shopDisplayName}</div>
                <div class="r-sub">
                  ${m.memberDisplayName}${m.memberCode ? ` · ${m.memberCode}` : ''}
                </div>
              </div>
              <div class="r-meta">
                <span>${when}</span>
                ${p.daySequence > 0 ? html`<span>Receipt #${p.daySequence}</span>` : nothing}
              </div>
              ${p.items.map((it) => {
                const mods = (it.modifiers ?? []).filter((x) => x.name);
                const modLabel = mods
                  .map(
                    (x) => `${x.name}${x.quantity > 1 ? ` ×${x.quantity}` : ''}`
                  )
                  .join(', ');
                return html`<div class="r-line">
                  <div class="r-line-main">
                    <span class="r-qty">${it.quantity}×</span>
                    <span class="r-name"
                      >${it.name}${it.variantLabel ? ` (${it.variantLabel})` : ''}</span
                    >
                    <span class="r-amt"
                      >${money(it.unitPrice * it.quantity)}</span
                    >
                  </div>
                  ${modLabel ? html`<div class="r-mods">${modLabel}</div>` : nothing}
                </div>`;
              })}
              <div class="r-totals">
                <div>
                  <span>Subtotal</span><span>${money(p.subtotal)}</span>
                </div>
                ${
                  p.lineDiscountTotal > 0
                    ? html`<div>
                        <span>Discounts</span
                        ><span>−${money(p.lineDiscountTotal)}</span>
                      </div>`
                    : nothing
                }
                ${
                  p.cartDiscountAmount > 0
                    ? html`<div>
                        <span>Cart discount</span
                        ><span>−${money(p.cartDiscountAmount)}</span>
                      </div>`
                    : nothing
                }
                ${p.tax > 0 ? html`<div><span>Tax</span><span>${money(p.tax)}</span></div>` : nothing}
                <div class="r-grand">
                  <span>Total</span><span>${money(p.total)}</span>
                </div>
                <div class="r-pay">
                  <span>Paid by</span><span>${p.paymentMethod}</span>
                </div>
              </div>
              <div class="r-foot">Thank you!</div>
            </div>
          </div>
          <div class="receipt-actions">
            <button class="r-btn ghost" @click=${this._closeReceipt}>
              Close
            </button>
            <button class="r-btn solid" @click=${this._printReceipt}>
              <sl-icon name="printer"></sl-icon> Save / Print PDF
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // Print the open receipt as an isolated document (browser "Save as PDF").
  // Rendered into a hidden iframe so the wallet's own styles never leak in and
  // only the receipt prints — no app-wide print CSS needed.
  private _printReceipt() {
    const p = this._receipt;
    const m = this._receiptMembership;
    if (!p || !m) return;
    const esc = (s: string) =>
      s.replace(
        /[&<>"]/g,
        (c) =>
          ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[
            c
          ] as string
      );
    const when = new Date(p.createdUtc).toLocaleString();
    const lines = p.items
      .map((it) => {
        const mods = (it.modifiers ?? [])
          .filter((x) => x.name)
          .map(
            (x) =>
              `${esc(x.name as string)}${x.quantity > 1 ? ` x${x.quantity}` : ''}`
          )
          .join(', ');
        return `<div class="ln"><span>${it.quantity}× ${esc(it.name)}${
          it.variantLabel ? ` (${esc(it.variantLabel)})` : ''
        }${mods ? `<br><small>${mods}</small>` : ''}</span><span>${money(
          it.unitPrice * it.quantity
        )}</span></div>`;
      })
      .join('');
    const totals = [
      `<div class="tl"><span>Subtotal</span><span>${money(p.subtotal)}</span></div>`,
      p.lineDiscountTotal > 0
        ? `<div class="tl"><span>Discounts</span><span>-${money(p.lineDiscountTotal)}</span></div>`
        : '',
      p.cartDiscountAmount > 0
        ? `<div class="tl"><span>Cart discount</span><span>-${money(p.cartDiscountAmount)}</span></div>`
        : '',
      p.tax > 0
        ? `<div class="tl"><span>Tax</span><span>${money(p.tax)}</span></div>`
        : '',
      `<div class="tl grand"><span>Total</span><span>${money(p.total)}</span></div>`,
      `<div class="tl"><span>Paid by</span><span>${esc(p.paymentMethod)}</span></div>`,
    ].join('');
    const doc = `<!doctype html><html><head><meta charset="utf-8"><title>Receipt</title><style>
      body { margin: 0; padding: 16px; color: #111; font-family: ui-monospace, 'Courier New', monospace; }
      .r { max-width: 320px; margin: 0 auto; }
      .head { text-align: center; margin-bottom: 8px; }
      .shop { font-size: 18px; font-weight: 700; }
      .sub { font-size: 12px; color: #444; }
      .meta { display: flex; justify-content: space-between; font-size: 12px; color: #444; border-bottom: 1px dashed #999; padding-bottom: 6px; margin-bottom: 6px; }
      .ln { display: flex; justify-content: space-between; gap: 10px; font-size: 13px; padding: 3px 0; }
      .ln small { color: #555; }
      .totals { border-top: 1px dashed #999; margin-top: 6px; padding-top: 6px; }
      .tl { display: flex; justify-content: space-between; font-size: 13px; padding: 2px 0; }
      .grand { font-weight: 700; font-size: 15px; border-top: 1px solid #111; margin-top: 4px; padding-top: 4px; }
      .foot { text-align: center; margin-top: 10px; font-size: 12px; color: #444; }
    </style></head><body><div class="r">
      <div class="head"><div class="shop">${esc(m.shopDisplayName)}</div>
      <div class="sub">${esc(m.memberDisplayName)}${m.memberCode ? ` · ${esc(m.memberCode)}` : ''}</div></div>
      <div class="meta"><span>${esc(when)}</span>${p.daySequence > 0 ? `<span>#${p.daySequence}</span>` : ''}</div>
      ${lines}
      <div class="totals">${totals}</div>
      <div class="foot">Thank you!</div>
    </div></body></html>`;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
      'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const win = iframe.contentWindow;
    if (!win) {
      iframe.remove();
      return;
    }
    win.document.open();
    win.document.write(doc);
    win.document.close();
    // Let the iframe lay out, print once, then clean up.
    setTimeout(() => {
      win.focus();
      win.print();
      setTimeout(() => iframe.remove(), 1000);
    }, 250);
  }

  // A customer can also be a shop owner. Give them a way off the wallet to
  // create a shop (or a sample shop) — otherwise a customer with no shop is
  // stranded here with no setup path.
  private _goCreateShop() {
    setUserContextMode('shop');
    router.navigate(resolveRouterPath('shop/sample'));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'customer-home': CustomerHome;
  }
}
